import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Button,
  TextField,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Box,
} from '@mui/material';
import { AddShoppingCart as AddShoppingCartIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ORDER_MODES } from '../../constants/appConstants';
import { roundCarat2dp } from '../../utils/orderItemDisplay';
import {
  formatLotAvailableForClient,
  getNonCertLotClientCardTitle,
} from '../../utils/nonCertLotDisplay';
import { formatCurrency } from '../../utils/currency';

/**
 * Add-to-cart dialog for non-certified lots. Supports ordering by weight (ct)
 * or by stone count when caratPerStone is set on the lot.
 */
export default function ReserveLotDialog({
  open, lot, isClient, allowOrderOverStock = false, onClose, onConfirm,
}) {
  const { t } = useTranslation();
  const [mode, setMode] = useState(ORDER_MODES.BY_WEIGHT);
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  const canOrderByCount = lot?.caratPerStone > 0;

  useEffect(() => {
    if (!open) return;
    setMode(ORDER_MODES.BY_WEIGHT);
    setValue('');
    setError('');
    const id = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [open, lot?._id]);

  const parsedCount = useMemo(() => {
    const n = parseInt(value, 10);
    return Number.isInteger(n) && n > 0 ? n : null;
  }, [value]);

  const estimatedWeight = useMemo(() => {
    if (mode !== ORDER_MODES.BY_COUNT || !parsedCount || !lot?.caratPerStone) return null;
    return roundCarat2dp(parsedCount * lot.caratPerStone);
  }, [mode, parsedCount, lot?.caratPerStone]);

  const estimatedPrice = useMemo(() => {
    if (estimatedWeight == null || !lot?.pricePerCarat) return null;
    return Math.round(estimatedWeight * lot.pricePerCarat * 100) / 100;
  }, [estimatedWeight, lot?.pricePerCarat]);

  const maxStones = useMemo(() => {
    if (!lot?.caratPerStone || !lot?.availableCarat) return null;
    return Math.floor(lot.availableCarat / lot.caratPerStone);
  }, [lot?.caratPerStone, lot?.availableCarat]);

  const handleAdd = () => {
    if (!lot) return;

    if (mode === ORDER_MODES.BY_COUNT) {
      if (!parsedCount) {
        setError(t('inventory.invalidStoneCount'));
        return;
      }
      if (!allowOrderOverStock && estimatedWeight > lot.availableCarat) {
        setError(t('inventory.countNotAvailable', {
          count: parsedCount,
          available: lot.availableCarat,
          availableStones: maxStones ?? 0,
        }));
        return;
      }
      onConfirm(lot, estimatedWeight, { stoneCount: parsedCount, orderMode: ORDER_MODES.BY_COUNT });
      return;
    }

    const raw = parseFloat(value);
    if (Number.isNaN(raw) || raw <= 0) {
      setError(t('inventory.invalidCaratAmount'));
      return;
    }
    const parsed = roundCarat2dp(raw);
    if (parsed <= 0) {
      setError(t('inventory.invalidCaratAmount'));
      return;
    }
    if (!allowOrderOverStock && parsed > lot.availableCarat) {
      setError(t('inventory.maxCaratAvailable', { max: lot.availableCarat }));
      return;
    }
    onConfirm(lot, parsed);
  };

  const handleModeChange = (_, nextMode) => {
    if (!nextMode) return;
    setMode(nextMode);
    setValue('');
    setError('');
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const isAddDisabled = mode === ORDER_MODES.BY_COUNT
    ? !parsedCount
    : !value || parseFloat(value) <= 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('inventory.addToCart')}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {lot && (
          <>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {isClient ? (
                <>
                  <strong>{getNonCertLotClientCardTitle(lot)}</strong>
                  {' • '}
                  {t('inventory.col.available')}: <strong>{formatLotAvailableForClient(lot.availableCarat)}</strong>
                  {' • '}{formatCurrency(lot.pricePerCarat, lot.currency)}/ct
                </>
              ) : (
                <>
                  <strong>{lot.shape}</strong>
                  {' • '}
                  {lot.length != null && lot.width != null
                    ? `${lot.length}×${lot.width}`
                    : (lot.measurement || '—')}
                  {' • '}
                  {t('inventory.col.available')}: <strong>{lot.availableCarat} ct</strong>
                  {' • '}{formatCurrency(lot.pricePerCarat, lot.currency)}/ct
                </>
              )}
            </Typography>

            {allowOrderOverStock && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {t('settings.inventory.lots.oversellNote')}
              </Typography>
            )}
            {!canOrderByCount && lot && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {t('inventory.noCtsPerStone')}
              </Typography>
            )}
            {canOrderByCount && (
              <Box sx={{ mb: 2 }}>
                <ToggleButtonGroup
                  value={mode}
                  exclusive
                  onChange={handleModeChange}
                  size="small"
                  fullWidth
                >
                  <ToggleButton value={ORDER_MODES.BY_WEIGHT}>
                    {t('inventory.orderByWeight')}
                  </ToggleButton>
                  <ToggleButton value={ORDER_MODES.BY_COUNT}>
                    {t('inventory.orderByCount')}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}

            {mode === ORDER_MODES.BY_COUNT ? (
              <>
                <TextField
                  fullWidth
                  size="small"
                  label={t('inventory.stoneCount')}
                  type="number"
                  value={value}
                  inputRef={inputRef}
                  onChange={(e) => {
                    setValue(e.target.value);
                    if (error) setError('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAdd();
                    }
                  }}
                  inputProps={{
                    step: 1,
                    min: 1,
                    max: allowOrderOverStock ? undefined : (maxStones ?? undefined),
                  }}
                />
                {parsedCount && estimatedWeight != null && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                    {t('inventory.estimatedWeightValue', {
                      weight: estimatedWeight,
                      count: parsedCount,
                      carat: lot.caratPerStone,
                    })}
                  </Typography>
                )}
                {estimatedPrice != null && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {t('inventory.estimatedPrice')}: ~{formatCurrency(estimatedPrice, lot?.currency)}
                  </Typography>
                )}
              </>
            ) : (
              <TextField
                fullWidth
                size="small"
                label={t('inventory.carat')}
                type="number"
                value={value}
                inputRef={inputRef}
                onChange={(e) => {
                  setValue(e.target.value);
                  if (error) setError('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAdd();
                  }
                }}
                inputProps={{
                  step: 0.01,
                  min: 0,
                  max: allowOrderOverStock ? undefined : lot.availableCarat,
                }}
              />
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('inventory.cancel')}</Button>
        <Button
          onClick={handleAdd}
          variant="contained"
          disabled={isAddDisabled}
          startIcon={<AddShoppingCartIcon />}
        >
          {t('inventory.addToCart')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
